import React, { useState } from "react";

import { Card, Segmented, Table, ExternalLink } from "../../../Ui/index.jsx";
import { AdminTab, TabLoading } from "./adminShared.jsx";

// Defaulters: who is skipping the task code in commits / not filing EODs. One row per person, newest slip
// first. No daily alert goes to super-admin for any of this; this page is where it is read. Zero is a blank
// cell, not "0", and the cell stays in its column so the table keeps its shape.
const WINDOWS = [{ value: 7, label: "7 days" }, { value: 30, label: "30 days" }, { value: 90, label: "90 days" }];

function defaultAgo(iso) {
  if (!iso) return "";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
}

function PersonDetail({ person }) {
  return (
    <div className="Adm-Stack">
      {person.github.recent.length > 0 && (
        <div>
          <h4 className="ds-label">Recent commits with no task code</h4>
          <ul className="Adm-List Adm-Commits">
            {person.github.recent.map((c) => (
              <li key={c.sha + c.repository}>
                <ExternalLink className="ds-link" href={c.url}>{c.sha}</ExternalLink>
                <span>{c.message || "(no message)"}</span>
                <small className="ds-small">{c.repository}{c.branch ? ` · ${c.branch}` : ""} · {defaultAgo(c.at)}</small>
              </li>
            ))}
          </ul>
          {person.github.count > person.github.recent.length && <small className="ds-small">and {person.github.count - person.github.recent.length} more</small>}
        </div>
      )}
      {person.eod.dates.length > 0 && (
        <div>
          <h4 className="ds-label">Days with no EOD</h4>
          <p>{person.eod.dates.map((d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" })).join(", ")}{person.eod.count > person.eod.dates.length ? ` and ${person.eod.count - person.eod.dates.length} earlier` : ""}</p>
        </div>
      )}
    </div>
  );
}

export function DefaultersTab({ report, days, setDays }) {
  const [open, setOpen] = useState(null);
  const people = report?.people || [];
  const unmapped = report?.unmapped || [];
  const commits = people.reduce((n, p) => n + p.github.count, 0);
  const eods = people.reduce((n, p) => n + p.eod.count, 0);

  const columns = [
    { key: "name", header: "Person", className: "is-card-title", render: (p) => <span className="ds-strong">{p.name}</span> },
    { key: "department", header: "Department", render: (p) => p.department || "—" },
    { key: "commits", header: `Commits without a task code${commits ? ` (${commits})` : ""}`, align: "right", render: (p) => p.github.count || "" },
    { key: "eods", header: `EODs missed${eods ? ` (${eods})` : ""}`, align: "right", render: (p) => p.eod.count || "" },
    { key: "last", header: "Last default", className: "is-card-amount", render: (p) => defaultAgo(p.last_default_at) },
  ];

  return (
    <AdminTab title="Defaulters"
      subtitle="Commits pushed without a task code, and EODs not filed. Each person is told once a day; you are not."
      actions={<Segmented label="Window" options={WINDOWS} value={days} onChange={setDays} />}>
      {!report ? <TabLoading /> : (
        <>
          {!people.length && !unmapped.length ? (
            <p className="ds-muted">No defaults in the last {days} days.</p>
          ) : people.length > 0 && (
            <p className="ds-muted">{people.length} {people.length === 1 ? "person" : "people"} slipped in the last {days} days, most recent first.</p>
          )}

          {people.length > 0 && (
            <Table stack columns={columns} rows={people.map((p) => ({ ...p, id: p.employee_id }))}
              onRowClick={(p) => setOpen(open === p.employee_id ? null : p.employee_id)}
              renderDetail={(p) => (open === p.employee_id ? <PersonDetail person={p} /> : null)} />
          )}

          {unmapped.length > 0 && (
            <Card title="Pushed by GitHub accounts we cannot match to anyone"
              subtitle="Nobody has these GitHub usernames on their profile, so no one can be told. Add the username to the right person and the next scan will attribute them.">
              <Table flush stack rows={unmapped.map((u) => ({ ...u, id: u.github_login }))} columns={[
                { key: "github_login", header: "Account", className: "is-card-title", render: (u) => <span className="ds-strong">{u.github_login}</span> },
                { key: "count", header: "Commits", align: "right", className: "is-card-amount", render: (u) => `${u.count} ${u.count === 1 ? "commit" : "commits"}` },
                { key: "last", header: "Last", align: "right", render: (u) => defaultAgo(u.last_at) },
              ]} />
            </Card>
          )}
        </>
      )}
    </AdminTab>
  );
}
